import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import Navigation from '@/components/Navigation';
import PatientSelector from '@/components/PatientSelector';
import QuickActions from '@/components/QuickActions';
import QuickActionsPanel from '@/components/QuickActionsPanel';
import PatientInfo from '@/components/PatientInfo';
import PastSymptoms from '@/components/PastSymptoms';
import MedicationManager from '@/components/MedicationManager';
import NotesDialog from '@/components/NotesDialog';
import BloodPressureDialog from '@/components/BloodPressureDialog';
import BloodPressureHistory from '@/components/BloodPressureHistory';
import BloodSugarDialog from '@/components/BloodSugarDialog';
import BloodSugarHistory from '@/components/BloodSugarHistory';
import CriticalEventsCard from '@/components/CriticalEventsCard';
import CriticalEventDialog from '@/components/CriticalEventDialog';
import PricingBanner from '@/components/PricingBanner';
import MoodTracker from '@/components/MoodTracker';
import WeightTracker from '@/components/WeightTracker';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePatientContext } from '@/contexts/PatientContext';
import { Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { selectedPatientId, setSelectedPatientId } = usePatientContext();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [medications, setMedications] = useState([]);
  const [medicationLogs, setMedicationLogs] = useState([]);
  const [todaySymptoms, setTodaySymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSymptom, setShowAddSymptom] = useState(false);
  const [showMedicationManager, setShowMedicationManager] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showBloodPressureDialog, setShowBloodPressureDialog] = useState(false);
  const [showBloodSugarDialog, setShowBloodSugarDialog] = useState(false);
  const [editingSymptom, setEditingSymptom] = useState(null);
  const [editingBloodPressure, setEditingBloodPressure] = useState(null);
  const [editingBloodSugar, setEditingBloodSugar] = useState(null);
  const [bloodSugarRecords, setBloodSugarRecords] = useState([]);
  const [criticalEvents, setCriticalEvents] = useState([]);
  const [showCriticalEventDialog, setShowCriticalEventDialog] = useState(false);
  const [editingCriticalEvent, setEditingCriticalEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [symptomForm, setSymptomForm] = useState({
    date: new Date().toISOString().split('T')[0],
    symptoms: [],
    customSymptom: '',
  });

  const defaultSymptoms = ['Fatigue', 'Nausea', 'Pain', 'Loss of appetite', 'Sleep issues'];
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    fetchPatients();
    fetchSubscriptionStatus();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchDashboardData();
    }
  }, [selectedPatient, selectedDate]);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/patients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPatients(response.data);
      
      // Use the stored patient ID if available, otherwise use first patient
      if (response.data.length > 0) {
        if (selectedPatientId) {
          const storedPatient = response.data.find(p => p.id === selectedPatientId);
          if (storedPatient) {
            setSelectedPatient(storedPatient);
          } else {
            // If stored patient not found, use first patient
            setSelectedPatient(response.data[0]);
            setSelectedPatientId(response.data[0].id);
          }
        } else {
          // No stored patient, use first patient
          setSelectedPatient(response.data[0]);
          setSelectedPatientId(response.data[0].id);
        }
      }
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load patients');
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSelectedPatientId(patient.id);
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [symptomsRes, medsRes, logsRes, bsRes, ceRes] = await Promise.all([
        axios.get(`${API}/symptoms/${selectedPatient.id}`, { headers }),
        axios.get(`${API}/medications/${selectedPatient.id}`, { headers }),
        axios.get(`${API}/medication-logs/${selectedPatient.id}?date=${selectedDate}`, { headers }),
        axios.get(`${API}/blood-sugar/${selectedPatient.id}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/critical-events/${selectedPatient.id}`, { headers }).catch(() => ({ data: [] })),
      ]);

      setSymptoms(symptomsRes.data);
      setMedications(medsRes.data);
      setMedicationLogs(logsRes.data);
      setBloodSugarRecords(bsRes.data);
      setCriticalEvents(ceRes.data);

      const selectedDateSymptomEntry = symptomsRes.data.find((s) => s.date === selectedDate);
      if (selectedDateSymptomEntry) {
        setTodaySymptoms(selectedDateSymptomEntry.symptoms);
      } else {
        setTodaySymptoms([]);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const handleSymptomToggle = async (symptom) => {
    const token = localStorage.getItem('token');
    let newSymptoms;

    if (todaySymptoms.includes(symptom)) {
      newSymptoms = todaySymptoms.filter((s) => s !== symptom);
    } else {
      newSymptoms = [...todaySymptoms, symptom];
    }

    setTodaySymptoms(newSymptoms);

    try {
      const existingEntry = symptoms.find((s) => s.date === selectedDate);
      if (existingEntry) {
        await axios.put(
          `${API}/symptoms/${existingEntry.id}`,
          { patient_id: selectedPatient.id, date: selectedDate, symptoms: newSymptoms, notes: '' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/symptoms`,
          { patient_id: selectedPatient.id, date: selectedDate, symptoms: newSymptoms, notes: '' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success('Symptoms updated');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update symptoms');
    }
  };

  const handleMedicationTaken = async (medication, time, currentlyTaken = false) => {
    const token = localStorage.getItem('token');
    try {
      if (currentlyTaken) {
        // Find the log entry and update it to mark as not taken
        const log = medicationLogs.find(
          (log) => log.medication_id === medication.id && log.time === time && log.taken
        );
        if (log) {
          await axios.put(
            `${API}/medication-logs/${log.id}`,
            { ...log, taken: false },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Medication unmarked');
        }
      } else {
        // Mark as taken
        await axios.post(
          `${API}/medication-logs`,
          {
            medication_id: medication.id,
            patient_id: selectedPatient.id,
            date: selectedDate,
            time: time,
            taken: true,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Medication marked as taken');
      }
      fetchDashboardData();
    } catch (error) {
      toast.error(currentlyTaken ? 'Failed to unmark medication' : 'Failed to log medication');
    }
  };

  const handleAddSymptomFromPanel = () => {
    setShowAddSymptom(true);
  };

  const handleAddMedicationFromPanel = () => {
    setShowMedicationManager(true);
  };

  const handleAddAppointmentFromPanel = () => {
    navigate('/calendar');
  };

  const handleAddNoteFromPanel = () => {
    setShowNotesDialog(true);
  };

  const handleAddBloodPressureFromPanel = () => {
    setShowBloodPressureDialog(true);
  };



  const toggleSymptomInForm = (symptom) => {
    if (symptomForm.symptoms.includes(symptom)) {
      setSymptomForm({
        ...symptomForm,
        symptoms: symptomForm.symptoms.filter((s) => s !== symptom),
      });
    } else {
      setSymptomForm({
        ...symptomForm,
        symptoms: [...symptomForm.symptoms, symptom],
      });
    }
  };

  const addCustomSymptomToForm = () => {
    if (symptomForm.customSymptom.trim()) {
      setSymptomForm({
        ...symptomForm,
        symptoms: [...symptomForm.symptoms, symptomForm.customSymptom.trim()],
        customSymptom: '',
      });
    }
  };

  const handleSubmitSymptom = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      if (editingSymptom) {
        // Update existing symptom
        await axios.put(
          `${API}/symptoms/${editingSymptom.id}`,
          {
            date: symptomForm.date,
            symptoms: symptomForm.symptoms,
            notes: editingSymptom.notes || '',
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Symptom updated');
      } else {
        // Create new symptom
        await axios.post(
          `${API}/symptoms`,
          {
            patient_id: selectedPatient.id,
            date: symptomForm.date,
            symptoms: symptomForm.symptoms,
            notes: '',
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Symptom added');
      }
      
      setShowAddSymptom(false);
      setEditingSymptom(null);
      setSymptomForm({ date: selectedDate, symptoms: [], customSymptom: '' });
      fetchDashboardData();
    } catch (error) {
      toast.error(editingSymptom ? 'Failed to update symptom' : 'Failed to add symptom');
    }
  };

  const getHealthSummary = () => {
    const symptomsToday = symptoms.filter(s => s.date === selectedDate).length;
    const medsTaken = medicationLogs.filter(log => log.date === selectedDate && log.taken).length;
    return `${symptomsToday} symptom${symptomsToday !== 1 ? 's' : ''} logged, ${medsTaken} med${medsTaken !== 1 ? 's' : ''} taken`;
  };


  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!selectedPatient) {
    return (
      <div className="min-h-screen p-4">
        <Navigation active="home" />
        <div className="max-w-4xl mx-auto pt-8">
          <div className="flex justify-end mb-4">
            <PatientSelector
              patients={patients}
              selectedPatient={null}
              onSelectPatient={handlePatientSelect}
              onPatientsChange={fetchPatients}
            />
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">No patients found</h2>
            <p className="text-gray-600 mb-6">Click the + button above to add your first patient</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-8" data-testid="dashboard" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Decorative Background Elements */}
      <div style={{ position: 'absolute', top: '10%', right: '5%', width: '150px', height: '150px', background: 'radial-gradient(circle, rgba(162, 218, 211, 0.1) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }}></div>
      <div style={{ position: 'absolute', top: '30%', left: '3%', width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(249, 167, 167, 0.08) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }}></div>
      <div style={{ position: 'absolute', bottom: '20%', right: '15%', width: '80px', height: '80px', background: 'radial-gradient(circle, rgba(162, 218, 211, 0.08) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }}></div>
      
      <Navigation active="home" />
      
      <div className="max-w-6xl mx-auto p-4 md:p-8" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header with Sunshine Icon */}
        <div className="flex items-center justify-between mb-8">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="relative">
            {/* Sunshine Icon */}
            <div style={{ position: 'absolute', top: '-20px', right: '-40px', width: '60px', height: '60px', opacity: 0.3 }}>
              <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50" cy="50" r="20" fill="#F9A7A7" opacity="0.4"/>
                <g stroke="#F9A7A7" strokeWidth="3" opacity="0.3">
                  <line x1="50" y1="10" x2="50" y2="25" />
                  <line x1="50" y1="75" x2="50" y2="90" />
                  <line x1="10" y1="50" x2="25" y2="50" />
                  <line x1="75" y1="50" x2="90" y2="50" />
                  <line x1="20" y1="20" x2="32" y2="32" />
                  <line x1="68" y1="68" x2="80" y2="80" />
                  <line x1="80" y1="20" x2="68" y2="32" />
                  <line x1="32" y1="68" x2="20" y2="80" />
                </g>
              </svg>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold" style={{ color: '#333' }}>Hi {user.name} 👋</h1>
            <p className="text-gray-600 mt-1">How is {selectedPatient.name} doing?</p>
            
            {/* Date Selector */}
            <div className="mt-3 flex items-center gap-3">
              <Calendar size={18} style={{ color: '#A2DAD3' }} />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={format(new Date(), 'yyyy-MM-dd')}
                min={format(new Date(new Date().setMonth(new Date().getMonth() - 4)), 'yyyy-MM-dd')}
                className="input text-sm px-3 py-1"
                style={{ border: '2px solid #A2DAD3' }}
              />
              <span className="text-xs text-gray-500">(Past 4 months)</span>
            </div>
            
            <div className="mt-2 space-y-1">
              <p className="text-sm text-gray-500">{format(new Date(selectedDate), 'EEEE, MMMM d, yyyy')}</p>
              <p className="text-sm font-medium" style={{ color: '#A2DAD3' }}>{getHealthSummary()}</p>
            </div>
            
            {/* Small Floral Element */}
            <div style={{ position: 'absolute', bottom: '-15px', left: '-25px', opacity: 0.2 }}>
              <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="20" cy="20" rx="8" ry="12" fill="#A2DAD3" transform="rotate(0 20 20)" opacity="0.3"/>
                <ellipse cx="20" cy="20" rx="8" ry="12" fill="#A2DAD3" transform="rotate(60 20 20)" opacity="0.3"/>
                <ellipse cx="20" cy="20" rx="8" ry="12" fill="#A2DAD3" transform="rotate(120 20 20)" opacity="0.3"/>
                <circle cx="20" cy="20" r="4" fill="#F9A7A7" opacity="0.4"/>
              </svg>
            </div>
          </motion.div>
          <PatientSelector
            patients={patients}
            selectedPatient={selectedPatient}
            onSelectPatient={handlePatientSelect}
            onPatientsChange={fetchPatients}
          />
        </div>

        {/* Patient Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6"
        >
          <PatientInfo patient={selectedPatient} onUpdate={fetchPatients} />
        </motion.div>

        {/* Pricing Banner for Free/Trial Users */}
        {subscriptionStatus && (
          <PricingBanner 
            subscriptionPlan={subscriptionStatus.plan}
            subscriptionStatus={subscriptionStatus.status}
            adminGrantedPremium={subscriptionStatus.admin_granted_premium}
            trialEndDate={subscriptionStatus.trial_end_date}
          />
        )}


        {/* Main Dashboard Grid - Responsive Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
          {/* Today's Symptoms */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card"
            data-testid="symptoms-card"
          >
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#A2DAD3' }}>
              {selectedDate === new Date().toISOString().split('T')[0] ? "Today's Symptoms" : `Symptoms for ${format(new Date(selectedDate), 'MMM d, yyyy')}`}
            </h2>
            <div className="space-y-3 mb-4">
              {defaultSymptoms.map((symptom) => (
                <div key={symptom} className="flex items-center space-x-3" data-testid={`symptom-${symptom.toLowerCase().replace(' ', '-')}`}>
                  <Checkbox
                    checked={todaySymptoms.includes(symptom)}
                    onCheckedChange={() => handleSymptomToggle(symptom)}
                    id={symptom}
                  />
                  <label htmlFor={symptom} className="text-gray-700 cursor-pointer">
                    {symptom}
                  </label>
                </div>
              ))}
            </div>

            {/* Coral box showing today's logged symptoms */}
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: '#fff5f5', border: '2px solid #F9A7A7' }}
              data-testid="logged-symptoms-today"
            >
              <p className="text-sm font-medium mb-2" style={{ color: '#F9A7A7' }}>Logged Today:</p>
              {todaySymptoms.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No symptoms logged yet today.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {todaySymptoms.map((symptom, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 rounded-full text-sm"
                      style={{ backgroundColor: '#F9A7A7', color: 'white' }}
                    >
                      {symptom}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Mood and Weight Trackers Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            <MoodTracker patient={selectedPatient} date={selectedDate} />
            <WeightTracker patient={selectedPatient} date={selectedDate} />
          </motion.div>

          {/* Critical Events */}
          <CriticalEventsCard 
            events={criticalEvents}
            selectedDate={selectedDate}
            onAdd={() => setShowCriticalEventDialog(true)}
            onEdit={(event) => {
              setEditingCriticalEvent(event);
              setShowCriticalEventDialog(true);
            }}
            onDelete={async (id) => {
              if (!window.confirm('Delete this critical event?')) return;
              try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API}/critical-events/${id}`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Critical event deleted');
                fetchDashboardData();
              } catch (error) {
                toast.error('Failed to delete');
              }
            }}
          />
        </div>

        {/* Quick Actions Panel - Full Width */}
        <div className="mt-6">
          <QuickActionsPanel
            onAddSymptom={handleAddSymptomFromPanel}
            onAddMedication={handleAddMedicationFromPanel}
            onAddAppointment={handleAddAppointmentFromPanel}
            onAddNote={handleAddNoteFromPanel}
            onAddBloodPressure={handleAddBloodPressureFromPanel}
            onAddBloodSugar={() => setShowBloodSugarDialog(true)}
          />
        </div>

        {/* Medications Grid */}
        <div className="grid grid-cols-1 gap-6 mt-6">

          {/* Today's Medications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card"
            data-testid="medications-card"
          >
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#A2DAD3' }}>Medications</h2>
            {medications.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No medications added yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {medications.filter(med => {
                  // Filter medications that are active on the selected date
                  if (!med.start_date || !med.end_date) return true; // Show if no dates set
                  return selectedDate >= med.start_date && selectedDate <= med.end_date;
                }).map((med) => {
                  // Check if medication is active on selected date
                  const isActive = !med.end_date || med.end_date >= selectedDate;
                  
                  return (
                    <div 
                      key={med.id} 
                      className="p-4 rounded-xl relative" 
                      style={{ 
                        backgroundColor: isActive ? '#f0f9f8' : '#f5f5f5', 
                        border: `2px solid ${isActive ? '#A2DAD3' : '#ddd'}`,
                        opacity: isActive ? 1 : 0.6
                      }}
                    >
                      {/* Action buttons */}
                      <div className="absolute top-2 right-2 flex gap-1">
                        <button
                          onClick={() => {
                            setEditingMedication(med);
                            setShowMedicationManager(true);
                          }}
                          className="text-xs px-2 py-1 rounded-full hover:opacity-80"
                          style={{ backgroundColor: '#A2DAD3', color: 'white' }}
                          title="Edit medication"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Delete this medication?')) return;
                            try {
                              const token = localStorage.getItem('token');
                              await axios.delete(`${API}/medications/${med.id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success('Medication deleted');
                              fetchDashboardData();
                            } catch (error) {
                              toast.error('Failed to delete medication');
                            }
                          }}
                          className="text-xs px-2 py-1 rounded-full hover:opacity-80"
                          style={{ backgroundColor: '#F9A7A7', color: 'white' }}
                          title="Delete medication"
                        >
                          🗑️
                        </button>
                      </div>
                      
                      {!isActive && (
                        <div className="absolute top-2 right-24 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#F9A7A7', color: 'white' }}>
                          Completed
                        </div>
                      )}
                      <h3 className="font-semibold text-lg mb-1 pr-28">{med.name}</h3>
                      {med.purpose && (
                        <p className="text-xs text-gray-500 mb-2 italic">For: {med.purpose}</p>
                      )}
                      <p className="text-sm text-gray-600 mb-2">{med.dosage}</p>
                      
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#A2DAD3', color: 'white' }}>
                          {med.frequency}
                        </span>
                        {med.duration && (
                          <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#F9A7A7', color: 'white' }}>
                            {med.duration}
                          </span>
                        )}
                      </div>
                      
                      {med.start_date && med.end_date && (
                        <p className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                          <span>📅</span>
                          {format(new Date(med.start_date), 'MMM d')} - {format(new Date(med.end_date), 'MMM d, yyyy')}
                        </p>
                      )}
                      
                      {med.notes && (
                        <p className="text-xs italic mb-3 p-2 rounded" style={{ backgroundColor: '#fff5f5', color: '#666' }}>
                          💡 {med.notes}
                        </p>
                      )}
                      
                      {med.reminder_times && med.reminder_times.length > 0 && (
                        <div className="space-y-2 mt-3 pt-3 border-t" style={{ borderColor: '#A2DAD3' }}>
                          <p className="text-xs font-medium text-gray-600 mb-2">
                            Times ({med.reminder_times.length}x/day):
                          </p>
                          {med.reminder_times.map((time) => {
                            const isTaken = medicationLogs.some(
                              (log) => log.medication_id === med.id && log.time === time && log.taken
                            );
                            return (
                              <div key={time} className="flex items-center justify-between">
                                <span className="text-sm font-medium">{time}</span>
                                {isTaken ? (
                                  <button
                                    data-testid={`undo-taken-${med.id}-${time}`}
                                    onClick={() => handleMedicationTaken(med, time, true)}
                                    className="text-green-600 text-sm font-medium hover:text-green-700 hover:underline cursor-pointer"
                                    title="Click to undo"
                                  >
                                    ✓ Taken (click to undo)
                                  </button>
                                ) : isActive ? (
                                  <button
                                    data-testid={`mark-taken-${med.id}-${time}`}
                                    onClick={() => handleMedicationTaken(med, time)}
                                    className="text-sm px-3 py-1 rounded-full"
                                    style={{ backgroundColor: '#A2DAD3', color: 'white' }}
                                  >
                                    Mark Taken
                                  </button>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* Past Symptoms History */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="md:col-span-2"
          >
            <PastSymptoms 
              symptoms={symptoms} 
              patientId={selectedPatient.id}
              onEdit={(symptom) => {
                setEditingSymptom(symptom);
                setSymptomForm({
                  date: symptom.date,
                  symptoms: symptom.symptoms,
                  customSymptom: '',
                });
                setTodaySymptoms(symptom.symptoms);
                setShowAddSymptom(true);
              }}
              onDelete={fetchDashboardData}
            />
          </motion.div>


        </div>
        {/* End of grid */}

        {/* Blood Pressure History - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6"
        >
          <BloodPressureHistory 
            patientId={selectedPatient.id} 
            onEdit={(record) => {
              setEditingBloodPressure(record);
              setShowBloodPressureDialog(true);
            }}
          />
        </motion.div>

        {/* Blood Sugar History - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mt-6"
        >
          <div className="card">
            <h3 className="text-xl font-semibold mb-4" style={{ color: '#F9A7A7' }}>
              Blood Sugar History
            </h3>
            {bloodSugarRecords.length === 0 ? (
              <p className="text-sm text-gray-500">No records yet</p>
            ) : (
              <BloodSugarHistory
                records={bloodSugarRecords}
                onUpdate={fetchDashboardData}
                onEdit={(record) => {
                  setEditingBloodSugar(record);
                  setShowBloodSugarDialog(true);
                }}
              />
            )}
          </div>
        </motion.div>

        {/* Footer */}
        <div className="app-footer">
          Made with care for caregivers
        </div>

      </div>
      {/* End of max-w container */}

      {/* Add/Edit Symptom Dialog */}
      <Dialog open={showAddSymptom} onOpenChange={(open) => {
        setShowAddSymptom(open);
        if (!open) setEditingSymptom(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSymptom ? 'Edit Symptom' : 'Add Symptom'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitSymptom} className="space-y-4" data-testid="add-symptom-form">
            <div>
              <label className="block text-sm font-medium mb-2">Date</label>
              <input
                data-testid="symptom-date-input"
                type="date"
                value={symptomForm.date}
                onChange={(e) => setSymptomForm({ ...symptomForm, date: e.target.value })}
                max={format(new Date(), 'yyyy-MM-dd')}
                min={format(new Date(new Date().setMonth(new Date().getMonth() - 4)), 'yyyy-MM-dd')}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Select Symptoms</label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {defaultSymptoms.map((symptom) => (
                  <button
                    key={symptom}
                    type="button"
                    onClick={() => toggleSymptomInForm(symptom)}
                    className="p-2 rounded-lg text-sm transition"
                    style={{
                      backgroundColor: symptomForm.symptoms.includes(symptom) ? '#A2DAD3' : '#f0f9f8',
                      color: symptomForm.symptoms.includes(symptom) ? 'white' : '#333',
                      border: `2px solid ${symptomForm.symptoms.includes(symptom) ? '#A2DAD3' : '#e5e5e5'}`,
                    }}
                  >
                    {symptom}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Other Symptoms</label>
              <div className="flex gap-2">
                <input
                  data-testid="custom-symptom-input"
                  type="text"
                  value={symptomForm.customSymptom}
                  onChange={(e) => setSymptomForm({ ...symptomForm, customSymptom: e.target.value })}
                  placeholder="Enter custom symptom"
                />
                <button
                  type="button"
                  onClick={addCustomSymptomToForm}
                  className="btn btn-primary px-3"
                >
                  Add
                </button>
              </div>
            </div>
            <button
              data-testid="submit-symptom-button"
              type="submit"
              className="btn btn-primary w-full"
              disabled={symptomForm.symptoms.length === 0}
            >
              Add Symptom
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <NotesDialog
        isOpen={showNotesDialog}
        onClose={() => setShowNotesDialog(false)}
        patientId={selectedPatient?.id}
        onUpdate={fetchDashboardData}
      />

      {/* Blood Pressure Dialog */}
      <BloodPressureDialog
        isOpen={showBloodPressureDialog}
        onClose={() => {
          setShowBloodPressureDialog(false);
          setEditingBloodPressure(null);
        }}
        patientId={selectedPatient?.id}
        onUpdate={fetchDashboardData}
        editingRecord={editingBloodPressure}
      />

      {/* Blood Sugar Dialog */}
      <BloodSugarDialog
        isOpen={showBloodSugarDialog}
        onClose={() => {
          setShowBloodSugarDialog(false);
          setEditingBloodSugar(null);
        }}
        patientId={selectedPatient?.id}
        onUpdate={fetchDashboardData}
        editingRecord={editingBloodSugar}
      />

      {/* Critical Event Dialog */}
      <CriticalEventDialog
        isOpen={showCriticalEventDialog}
        onClose={() => {
          setShowCriticalEventDialog(false);
          setEditingCriticalEvent(null);
        }}
        patientId={selectedPatient?.id}
        onUpdate={fetchDashboardData}
        editingEvent={editingCriticalEvent}
      />

      {/* Medication Manager Dialog */}
      <MedicationManager
        isOpen={showMedicationManager}
        onClose={() => {
          setShowMedicationManager(false);
          setEditingMedication(null);
        }}
        patientId={selectedPatient?.id}
        onUpdate={fetchDashboardData}
        editingMedication={editingMedication}
      />

      {/* Critical Event Dialog */}
      <CriticalEventDialog
        isOpen={showCriticalEventDialog}
        onClose={() => {
          setShowCriticalEventDialog(false);
          setEditingCriticalEvent(null);
        }}
        patientId={selectedPatient?.id}
        onUpdate={fetchDashboardData}
        editingEvent={editingCriticalEvent}
      />

      <QuickActions selectedPatient={selectedPatient} onUpdate={fetchDashboardData} />
      {/* End of min-h-screen */}
    </div>
  );
};

export default Dashboard;
